import React, { useState } from 'react';
import { ChevronDown, Menu, X, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import { t } from '../data/translations';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { openContactModal } = useContactModal();

  const navigationLinks = [
    {
      label: t('nav.services', language),
      href: '#services',
      dropdown: [
        { label: t('nav.temporaryEmployment', language), href: '/temporary' },
        { label: t('nav.recruitment', language), href: '/recruitment' },
        { label: t('nav.hrConsulting', language), href: '/consulting' }
      ]
    },
    { label: t('nav.aboutUs', language), href: '/about', isPage: true },
    { label: t('nav.contact', language), href: '#contact', isContact: true }
  ];

  const languages = [
    { code: 'sk', label: 'SK' },
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#e8a238]/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex flex-col">
            <span className="text-2xl font-bold text-[#131132]">
              <span className="font-light">Work</span>
              <span>Pro</span>
            </span>
            <span className="text-[10px] text-[#131132]/60 -mt-1">part of ALS Group</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigationLinks.map((link) => (
              <div
                key={link.label}
                className="relative group"
              >
                {link.isContact ? (
                  <button
                    onClick={openContactModal}
                    className="flex items-center gap-1 text-[#131132] font-medium hover:opacity-70 transition-opacity py-4"
                  >
                    {link.label}
                  </button>
                ) : link.isPage ? (
                  <Link
                    to={link.href}
                    className="flex items-center gap-1 text-[#131132] font-medium hover:opacity-70 transition-opacity py-4"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    className="flex items-center gap-1 text-[#131132] font-medium hover:opacity-70 transition-opacity py-4"
                  >
                    {link.label}
                    {link.dropdown && <ChevronDown className="w-4 h-4" />}
                  </a>
                )}
                
                {link.dropdown && (
                  <div className="absolute top-full left-0 pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-2 bg-white rounded-xl shadow-lg min-w-[220px] border border-gray-100">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          className="block px-4 py-3 text-[#131132] hover:bg-[#FEF3C7] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Language Switcher & CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Dropdown - Click based for stability */}
            <div className="relative">
              <button 
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 text-[#131132] font-medium hover:opacity-70 transition-opacity"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4" />
                {language.toUpperCase()}
                <ChevronDown className={`w-4 h-4 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {langDropdownOpen && (
                <>
                  {/* Invisible overlay to close dropdown */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setLangDropdownOpen(false)}
                  />
                  <div className="absolute top-full right-0 mt-1 py-2 bg-white rounded-xl shadow-lg min-w-[100px] z-50 border border-gray-100">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-[#FEF3C7] transition-colors ${
                          language === lang.code ? 'bg-[#FEF3C7] font-semibold' : ''
                        }`}
                      >
                        <span className="text-[#131132]">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={openContactModal}
              className="px-6 py-2.5 border-2 border-[#131132] text-[#131132] font-semibold rounded-lg hover:bg-[#131132] hover:text-white transition-all duration-300"
              data-testid="navbar-contact-button"
            >
              {t('nav.contact', language)}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#131132]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-[#131132]/20">
            {navigationLinks.map((link) => (
              link.isContact ? (
                <button
                  key={link.label}
                  onClick={() => {
                    openContactModal();
                    setIsMenuOpen(false);
                  }}
                  className="block py-3 text-[#131132] font-medium text-left w-full"
                >
                  {link.label}
                </button>
              ) : link.isPage ? (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-3 text-[#131132] font-medium"
                >
                  {link.label}
                </Link>
              ) : link.dropdown ? (
                <div key={link.label}>
                  <span className="block py-3 text-[#131132] font-medium">{link.label}</span>
                  <div className="pl-4">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="block py-2 text-[#131132]/70"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="block py-3 text-[#131132] font-medium"
                >
                  {link.label}
                </a>
              )
            ))}
            
            {/* Mobile Language Switcher */}
            <div className="flex gap-2 mt-4 mb-4">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    language === lang.code 
                      ? 'bg-[#131132] text-white' 
                      : 'bg-white/50 text-[#131132]'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
